import { MdClose } from "@react-icons/all-files/md/MdClose";
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";

import useUpdateMemberMemo from "@core/hooks/mutations/member/useUpdateMemberMemo";
import useMyInformation from "@core/hooks/queries/member/useMyInformation";
import queryKeyGenerator from "@core/utils/queryKeyGenerator";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const FloatingMemo = ({ isOpen, onClose }: Props) => {
  const queryClient = useQueryClient();
  const memoRef = useRef<HTMLTextAreaElement>(null);
  const myInfo = useMyInformation();

  const updateMemo = useUpdateMemberMemo({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeyGenerator.getMyInformation(),
      });
      toast.success("메모가 저장되었습니다.", { autoClose: 1500 });
    },
    onError: () => {
      toast.error("메모 저장에 실패했습니다.");
    },
  });

  const handleSave = () => {
    if (memoRef.current) {
      updateMemo.mutate(memoRef.current.value);
    }
  };

  return (
    <Panel $isOpen={isOpen}>
      <Header>
        <Title>계정 메모</Title>
        <CloseButton onClick={onClose}>
          <MdClose />
        </CloseButton>
      </Header>
      <Body>
        <Textarea
          ref={memoRef}
          key={`${isOpen}-${myInfo.data?.memo}`}
          defaultValue={myInfo.data?.memo ?? ""}
          placeholder="메모를 입력하세요"
          rows={6}
          maxLength={500}
        />
        <SaveButton onClick={handleSave} disabled={updateMemo.isPending}>
          {updateMemo.isPending ? "저장 중..." : "저장"}
        </SaveButton>
      </Body>
    </Panel>
  );
};

export default FloatingMemo;

const Panel = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 120px;
  right: 60px;
  width: 280px;
  background: ${({ theme }) => theme.app.bg.white};
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;

  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
  transform: translateY(${({ $isOpen }) => ($isOpen ? "0" : "20px")});
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.app.border};
`;

const Title = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.dark2};
`;

const CloseButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  cursor: pointer;
  border-radius: 4px;
  color: ${({ theme }) => theme.app.text.light1};

  &:hover {
    color: ${({ theme }) => theme.app.text.dark2};
  }

  svg {
    font-size: 18px;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.app.bg.gray1};
  color: ${({ theme }) => theme.app.text.dark2};
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  outline: none;
  font-family: inherit;

  &:focus {
    border-color: ${({ theme }) => theme.app.palette.blue[350]};
  }

  &::placeholder {
    color: ${({ theme }) => theme.app.text.light2};
  }
`;

const SaveButton = styled.button`
  padding: 6px 14px;
  background: ${({ theme }) => theme.app.palette.blue[350]};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-end;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
